import { MY_FILES_FOLDER_ID } from '../storage-folders/system';
import type { Attachment, AttachmentId, MockCache, StorageFolderId, TaskId } from '../types';

import { MUTATION_TABLE as T } from './registry';
import { bump, requireCache } from './runtime';
import { maxAttachmentBytes } from './mutations/comments';
import { synced } from './sync-hook';

export const UPLOADS_KEY = 'uploads';

export const addAttachment = synced('addAttachment', T.addAttachment.mode, T.addAttachment.run);
export const markAttachmentReady = synced('markAttachmentReady', T.markAttachmentReady.mode, T.markAttachmentReady.run);
export const setAttachmentThumbnail = synced('setAttachmentThumbnail', T.setAttachmentThumbnail.mode, T.setAttachmentThumbnail.run);

export interface FileTransport {
  upload(id: AttachmentId, file: Blob): void;
}

export interface ThumbnailTransport {
  upload(attachmentId: AttachmentId, image: Blob): void;
}

export type AttachFileResult = 'attached' | 'too-large';
export type UploadState = 'ready' | 'uploading' | 'failed' | 'interrupted';

interface PendingFile {
  file: Blob;
  objectUrl: string | null;
  failed: boolean;
}

interface UploadProgress {
  sentBytes: number;
  totalBytes: number;
  bumpedAt: number;
}

const PROGRESS_BUMP_INTERVAL_MS = 100;

const pendingFiles = new Map<AttachmentId, PendingFile>();
const uploadProgress = new Map<AttachmentId, UploadProgress>();
const submittedThumbnails = new Set<AttachmentId>();
let transport: FileTransport | null = null;
let thumbnailTransport: ThumbnailTransport | null = null;

export function setFileTransport(next: FileTransport | null): void {
  transport = next;
}

export function setThumbnailTransport(next: ThumbnailTransport | null): void {
  thumbnailTransport = next;
}

function createObjectUrl(file: Blob): string | null {
  return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null;
}

export function attachFile(args: {
  id: AttachmentId;
  ownerKind: Attachment['ownerKind'];
  ownerId: Attachment['ownerId'];
  file: File;
  taskId?: TaskId;
  folderId?: StorageFolderId | null;
}): AttachFileResult {
  const cache = requireCache();
  if (args.file.size > maxAttachmentBytes(cache)) return 'too-large';
  addAttachment({
    id: args.id,
    ownerKind: args.ownerKind,
    ownerId: args.ownerId,
    name: args.file.name,
    mime: args.file.type || 'application/octet-stream',
    sizeBytes: args.file.size,
    taskId: args.taskId,
    folderId: args.folderId,
  });
  pendingFiles.set(args.id, { file: args.file, objectUrl: createObjectUrl(args.file), failed: false });
  bump(UPLOADS_KEY);
  if (transport === null) {
    markAttachmentReady({ id: args.id });
  } else {
    transport.upload(args.id, args.file);
  }
  return 'attached';
}

export function uploadToStorage(args: { id: AttachmentId; file: File; folderId?: StorageFolderId | null }): AttachFileResult {
  return attachFile({
    id: args.id,
    ownerKind: 'storage',
    ownerId: requireCache().activeWorkspaceId,
    file: args.file,
    folderId: args.folderId === MY_FILES_FOLDER_ID ? null : (args.folderId ?? null),
  });
}

export function noteUploadFailed(id: AttachmentId): void {
  const entry = pendingFiles.get(id);
  if (entry === undefined) return;
  entry.failed = true;
  bump(UPLOADS_KEY);
}

export function noteUploadProgress(id: AttachmentId, sentBytes: number, totalBytes: number): void {
  const previous = uploadProgress.get(id);
  const now = Date.now();
  const due = previous === undefined || now - previous.bumpedAt >= PROGRESS_BUMP_INTERVAL_MS || sentBytes >= totalBytes;
  uploadProgress.set(id, { sentBytes, totalBytes, bumpedAt: due ? now : (previous?.bumpedAt ?? now) });
  if (due) bump(UPLOADS_KEY);
}

export function selectUploadProgress(id: AttachmentId): number | null {
  const entry = uploadProgress.get(id);
  if (entry === undefined || entry.totalBytes <= 0) return null;
  return Math.min(1, Math.max(0, entry.sentBytes / entry.totalBytes));
}

export function retryUpload(id: AttachmentId): boolean {
  const entry = pendingFiles.get(id);
  if (entry === undefined || !entry.failed || transport === null) return false;
  entry.failed = false;
  uploadProgress.delete(id);
  bump(UPLOADS_KEY);
  transport.upload(id, entry.file);
  return true;
}

export function selectUploadState(cache: MockCache, attachment: Attachment): UploadState {
  if (attachment.status === 'ready') return 'ready';
  const entry = pendingFiles.get(attachment.id);
  if (entry !== undefined) return entry.failed ? 'failed' : 'uploading';
  return attachment.uploaderId === cache.me ? 'interrupted' : 'uploading';
}

export function selectLocalUploads(cache: MockCache): Attachment[] {
  const uploads: Attachment[] = [];
  for (const id of pendingFiles.keys()) {
    const attachment = cache.attachments.get(id);
    if (attachment !== undefined) uploads.push(attachment);
  }
  return uploads;
}

export function attachmentFileHref(attachment: Attachment): string | null {
  if (attachment.status === 'ready' && attachment.url !== '') return attachment.url;
  return attachmentHref(attachment);
}

export function attachmentHref(attachment: Attachment): string | null {
  const objectUrl = pendingFiles.get(attachment.id)?.objectUrl;
  if (objectUrl !== undefined && objectUrl !== null) return objectUrl;
  return attachment.status === 'ready' && attachment.url !== '' ? attachment.url : null;
}

export function submitAttachmentThumbnail(attachmentId: AttachmentId, image: Blob): void {
  const cache = requireCache();
  if (cache.attachmentThumbnails.has(attachmentId) || submittedThumbnails.has(attachmentId)) return;
  submittedThumbnails.add(attachmentId);
  if (thumbnailTransport !== null) {
    thumbnailTransport.upload(attachmentId, image);
    return;
  }
  const url = createObjectUrl(image);
  if (url !== null) setAttachmentThumbnail({ attachmentId, url });
}

export function attachmentThumbnailHref(cache: MockCache, attachmentId: AttachmentId): string | null {
  return cache.attachmentThumbnails.get(attachmentId)?.url ?? null;
}
